import { useCallback, useRef, useState } from 'react';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Delete with an undo window, kept out of every client list until it is done.
 *
 * The delete reaches the database only when the window runs out, so for those
 * seconds the row still exists server-side. Any refetch inside the window
 * (returning to the tab refetches a list older than 30 s — App.tsx) used to put
 * it straight back on screen, and nothing refetched after the real delete, so it
 * stayed there until a reload (Dylan, 2026-10-22, screen-sharing over Zoom).
 * A row is now hidden by id while its delete is pending, whatever the lists
 * fetch, and every list is refetched before it is let go.
 */
export function useDelayedDelete(
  queryClient: QueryClient,
  listKeys: readonly QueryKey[],
  delayMs = 5000,
) {
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());
  const timers = useRef(new Map<string, { timer: ReturnType<typeof setTimeout>; ids: string[] }>());

  const release = useCallback((ids: string[]) => {
    setPending(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  const refreshLists = useCallback(
    () => Promise.all(listKeys.map(queryKey => queryClient.invalidateQueries({ queryKey }))),
    [queryClient, listKeys],
  );

  /** Hide `ids` now and run `run` once the window passes. */
  const schedule = useCallback((
    key: string,
    ids: string[],
    run: () => Promise<void>,
    onError?: (err: unknown) => void,
  ) => {
    setPending(prev => new Set([...prev, ...ids]));
    listKeys.forEach(queryKey => { void queryClient.cancelQueries({ queryKey }); });
    const timer = setTimeout(async () => {
      timers.current.delete(key);
      try {
        await run();
      } catch (err) {
        onError?.(err);
      } finally {
        // Refetch first, then show again: a failed delete comes back, a done one
        // is already gone from the fresh lists.
        await refreshLists().catch(() => {});
        release(ids);
      }
    }, delayMs);
    timers.current.set(key, { timer, ids });
  }, [queryClient, listKeys, delayMs, refreshLists, release]);

  /** Cancel a pending delete; its rows come back. */
  const undo = useCallback((key: string) => {
    const entry = timers.current.get(key);
    if (!entry) return;
    clearTimeout(entry.timer);
    timers.current.delete(key);
    release(entry.ids);
    void refreshLists();
  }, [refreshLists, release]);

  const hide = useCallback(<T extends { id: string }>(list: T[]): T[] =>
    pending.size === 0 ? list : list.filter(s => !pending.has(s.id)),
  [pending]);

  return { pending, hide, schedule, undo };
}
